import React from "react";
import { useSelector } from "react-redux";

const defaultLeftWidth = () => window.innerWidth >= 1600 ? 360 : window.innerWidth >= 1200 ? 320 : 288;

export default function ReadingLayout() {
  const left = useSelector(state => state.courseCurriculumOpen);
  const right = useSelector(state => state.screenListOpen);
  const drag = React.useRef(null);
  const [active, setActive] = React.useState(null);
  const [widths, setWidths] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("adReadingWidths"));
      const savedLeft = Number(saved?.left);
      const left = !savedLeft || (!saved?.leftCustomized && [248, 272].includes(savedLeft)) ? defaultLeftWidth() : savedLeft;
      return { left: Math.max(200, Math.min(400, left)), leftCustomized: !!saved?.leftCustomized, right: Math.max(180, Math.min(280, Number(saved?.right) || 216)) };
    } catch (_) { return { left: defaultLeftWidth(), right: 216 }; }
  });
  React.useEffect(() => {
    const layout = document.querySelector(".ad-workspace-layout");
    layout?.style.setProperty("--ad-left-rail", widths.left + "px");
    layout?.style.setProperty("--ad-right-rail", widths.right + "px");
    try { localStorage.setItem("adReadingWidths", JSON.stringify(widths)); } catch (_) {}
    window.dispatchEvent(new Event("resize"));
    return () => { layout?.style.removeProperty("--ad-left-rail"); layout?.style.removeProperty("--ad-right-rail"); };
  }, [widths]);
  React.useEffect(() => () => document.body.classList.remove("ad-resizing-sidebar"), []);
  const resize = (side, value) => setWidths(current => {
    const min = side === "left" ? 200 : 180;
    const otherWidth = side === "left" ? (right ? current.right : 0) : (left ? current.left : 0);
    const available = document.querySelector(".ad-workspace-layout")?.clientWidth || window.innerWidth;
    const max = Math.max(min, Math.min(side === "left" ? 400 : 280, available - otherWidth - 480));
    return { ...current, leftCustomized: current.leftCustomized || side === "left", [side]: Math.round(Math.max(min, Math.min(max, value))) };
  });
  const endDrag = () => {
    drag.current = null;
    setActive(null);
    document.body.classList.remove("ad-resizing-sidebar");
  };
  return <>{["left", "right"].map(side => (side === "left" ? left : right) &&
    <div key={side} className={`ad-sidebar-resizer ad-sidebar-resizer-${side}${active === side ? " is-dragging" : ""}`}
      role="separator" tabIndex={0} aria-orientation="vertical"
      aria-label={`Resize ${side === "left" ? "course" : "section"} sidebar`}
      aria-controls={side === "left" ? "curriculum-container" : "tutorial-menu-pillar"}
      aria-valuemin={side === "left" ? 200 : 180} aria-valuemax={side === "left" ? 400 : 280} aria-valuenow={widths[side]}
      title="Drag to resize · Double-click to reset · Arrow keys to adjust"
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { side, x: event.clientX, width: widths[side] };
        setActive(side);
        document.body.classList.add("ad-resizing-sidebar");
      }}
      onPointerMove={event => {
        if (drag.current?.side !== side) return;
        resize(side, drag.current.width + (event.clientX - drag.current.x) * (side === "left" ? 1 : -1));
      }}
      onPointerUp={event => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        endDrag();
      }}
      onPointerCancel={endDrag} onLostPointerCapture={endDrag}
      onDoubleClick={() => resize(side, side === "left" ? defaultLeftWidth() : 216)}
      onKeyDown={event => {
        if (event.key === "Home") { event.preventDefault(); resize(side, side === "left" ? defaultLeftWidth() : 216); }
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        resize(side, widths[side] + (event.key === "ArrowRight" ? 1 : -1) * (side === "left" ? 1 : -1) * (event.shiftKey ? 32 : 8));
      }}><span aria-hidden="true" /></div>
  )}</>;
}
